import React, { useState, useEffect } from 'react';
import axios from 'axios';
import IssuingControls from '../components/IssuingControls';
import PreauthPanel from '../components/PreauthPanel';
import AdminCateringPanel from '../components/AdminCateringPanel';
import MasterChefGalleryReview from '../components/MasterChefGalleryReview';

// Role Changer Component
const RoleChanger = ({ userId, currentRole, currentAdminLevel, userName, onUpdate }) => {
  const [selectedRole, setSelectedRole] = useState(currentRole);
  const [selectedAdminLevel, setSelectedAdminLevel] = useState('admin');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMasterCodeModal, setShowMasterCodeModal] = useState(false);
  const [masterCode, setMasterCode] = useState('');

  const handleRoleChange = async (code = '') => {
    if (selectedRole === currentRole) {
      setShowConfirm(false);
      setShowMasterCodeModal(false);
      return;
    }

    // If promoting to admin, need master code
    if (selectedRole === 'admin' && !code) {
      setShowMasterCodeModal(true);
      return;
    }

    setLoading(true);
    try {
      await axios.post('/auth/set-role', {
        user_id: userId,
        role: selectedRole,
        admin_level: selectedRole === 'admin' ? selectedAdminLevel : null,
        master_code: code || null
      });
      
      const levelText = selectedRole === 'admin' && selectedAdminLevel !== 'admin' 
        ? ` (${selectedAdminLevel.replace('_', ' ')})`
        : '';
      alert(`✅ ${userName}'s role changed to ${selectedRole}${levelText}`);
      onUpdate();
      setShowConfirm(false);
      setShowMasterCodeModal(false);
      setMasterCode('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to change role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <select
          value={selectedRole}
          onChange={(e) => {
            setSelectedRole(e.target.value);
            setShowConfirm(true);
          }}
          className="text-sm border border-gray-300 rounded px-2 py-1"
          data-testid={`role-select-${userId}`}
        >
          <option value="customer">Customer</option>
          <option value="chef">Chef</option>
          <option value="admin">Admin</option>
        </select>
        
        {selectedRole === 'admin' && showConfirm && (
          <select
            value={selectedAdminLevel}
            onChange={(e) => setSelectedAdminLevel(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
            data-testid={`admin-level-select-${userId}`}
          >
            <option value="admin">Regular Admin</option>
            <option value="admin_manager">Admin Manager</option>
          </select>
        )}
        
        {showConfirm && selectedRole !== currentRole && (
          <button
            onClick={() => handleRoleChange()}
            disabled={loading}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-xs"
            data-testid={`confirm-role-${userId}`}
          >
            {loading ? '...' : 'Confirm'}
          </button>
        )}
      </div>

      {/* Master Code Modal */}
      {showMasterCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">🔐 Master Admin Code Required</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter your master admin code (3357) to appoint <strong>{userName}</strong> as an admin.
            </p>
            <input
              type="password"
              value={masterCode}
              onChange={(e) => setMasterCode(e.target.value)}
              placeholder="Enter master code"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
              data-testid="master-code-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => handleRoleChange(masterCode)}
                disabled={loading || !masterCode}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="submit-master-code"
              >
                {loading ? 'Verifying...' : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setShowMasterCodeModal(false);
                  setMasterCode('');
                  setShowConfirm(false);
                }}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const AdminDashboard = ({ user, token }) => {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [chefProfiles, setChefProfiles] = useState({});
  const [bartenderProfiles, setBartenderProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, usersRes] = await Promise.all([
        axios.get('/admin/bookings'),
        axios.get('/admin/users')
      ]);
      setBookings(bookingsRes.data.bookings);
      setUsers(usersRes.data.users);
      
      // Fetch chef profiles for subscription status
      const chefUsers = usersRes.data.users.filter(u => u.role === 'chef');
      const profilesMap = {};
      for (const chef of chefUsers) {
        try {
          const profileRes = await axios.get(`/chef/profile/${chef.id}`);
          profilesMap[chef.id] = profileRes.data;
        } catch (err) {
          console.error(`Failed to fetch profile for chef ${chef.id}:`, err);
        }
      }
      setChefProfiles(profilesMap);
      
      // Fetch bartender profiles for subscription status
      const bartenderUsers = usersRes.data.users.filter(u => u.role === 'bartender');
      const bartenderProfilesMap = {};
      for (const bartender of bartenderUsers) {
        try {
          const profileRes = await axios.get(`/bartender/profile/${bartender.id}`);
          bartenderProfilesMap[bartender.id] = profileRes.data;
        } catch (err) {
          console.error(`Failed to fetch profile for bartender ${bartender.id}:`, err);
        }
      }
      setBartenderProfiles(bartenderProfilesMap);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">Manage bookings, users, and issuing cards</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Total Bookings</div>
          <div className="text-3xl font-bold text-orange-600">{bookings.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Total Users</div>
          <div className="text-3xl font-bold text-blue-600">{users.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Chefs</div>
          <div className="text-3xl font-bold text-green-600">
            {users.filter(u => u.role === 'chef').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm text-gray-600">Admins</div>
          <div className="text-3xl font-bold text-purple-600">
            {users.filter(u => u.role === 'admin').length}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bookings'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="bookings-tab"
          >
            All Bookings
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="users-tab"
          >
            Users
          </button>
          
          <button
            onClick={() => setActiveTab('catering')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'catering'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🍽️ Catering
          </button>
          
          {/* Chef Gallery Review - Master Admin Only */}
          {user.admin_level === 'master_admin' && (
            <button
              onClick={() => setActiveTab('chef_gallery')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'chef_gallery'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              👨‍🍳 Chef Gallery
            </button>
          )}
          
          {/* Banking Tab - Master Admin Only */}
          {user.admin_level === 'master_admin' && (
            <button
              onClick={() => setActiveTab('banking')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'banking'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              💰 Banking Settings
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'bookings' && (
        <div className="space-y-6" data-testid="bookings-section">
          {bookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Booking #{booking.id.substring(0, 8)}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {booking.meal_type} for {booking.guest_count} guests
                  </p>
                  <p className="text-sm text-gray-600">
                    📅 {new Date(booking.date).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    📍 {booking.service_city}, {booking.service_state} {booking.service_zip}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedBooking(selectedBooking?.id === booking.id ? null : booking)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  data-testid={`manage-booking-${booking.id}`}
                >
                  {selectedBooking?.id === booking.id ? 'Hide Controls' : 'Manage'}
                </button>
              </div>

              {selectedBooking?.id === booking.id && (
                <div className="mt-6 space-y-6 border-t pt-6">
                  <IssuingControls booking={booking} onUpdate={fetchData} />
                  <PreauthPanel booking={booking} onUpdate={fetchData} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Master Admin Test Accounts */}
          {user.admin_level === 'master_admin' && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg p-6">
              <h3 className="font-bold text-purple-900 mb-4">🔐 Master Admin Test Accounts</h3>
              <p className="text-sm text-purple-800 mb-4">
                Create test accounts with all bypasses (payment, subscription, SafeServ). Only you can do this!
              </p>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    const code = prompt('Enter master admin code (3357):');
                    if (!code) return;
                    try {
                      const response = await axios.post(`/admin/test-account/create?role=chef&master_code=${code}`);
                      alert(`✅ Test Chef Created!\n\nEmail: ${response.data.email}\nPassword: ${response.data.password}\n\nAll requirements bypassed!`);
                      fetchData();
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed');
                    }
                  }}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                  data-testid="create-test-chef"
                >
                  + Create Test Chef
                </button>
                <button
                  onClick={async () => {
                    const code = prompt('Enter master admin code (3357):');
                    if (!code) return;
                    try {
                      const response = await axios.post(`/admin/test-account/create?role=customer&master_code=${code}`);
                      alert(`✅ Test Customer Created!\n\nEmail: ${response.data.email}\nPassword: ${response.data.password}\n\nPayment bypassed!`);
                      fetchData();
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed');
                    }
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                  data-testid="create-test-customer"
                >
                  + Create Test Customer
                </button>
                <button
                  onClick={async () => {
                    const code = prompt('Enter master admin code (3357):');
                    if (!code) return;
                    try {
                      const response = await axios.post(`/admin/test-account/create?role=bartender&master_code=${code}`);
                      alert(`✅ Test Bartender Created!\n\nEmail: ${response.data.email}\nPassword: ${response.data.password}\n\nAll requirements bypassed!`);
                      fetchData();
                    } catch (err) {
                      alert(err.response?.data?.detail || 'Failed');
                    }
                  }}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
                  data-testid="create-test-bartender"
                >
                  + Create Test Bartender
                </button>
              </div>
              <div className="mt-4 pt-4 border-t border-purple-300">
                <p className="text-xs text-purple-700">
                  <strong>Bypasses:</strong> Chefs skip SafeServ cert, subscription, and all payments. 
                  Bartenders skip license verification, subscription, and all payments.
                  Customers skip all payments. Perfect for testing!
                </p>
              </div>
            </div>
          )}

          {/* Add Real Chef - Master Admin Only */}
          {user.admin_level === 'master_admin' && (
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-lg p-6">
              <h3 className="font-bold text-orange-900 mb-4">👨‍🍳 Add Real Chef</h3>
              <p className="text-sm text-orange-800 mb-4">
                Create a new chef account for a real professional chef joining your platform.
              </p>
              <button
                onClick={async () => {
                  const name = prompt('Enter chef full name:');
                  if (!name) return;
                  
                  const email = prompt('Enter chef email:');
                  if (!email) return;
                  
                  const password = prompt('Create a password for this chef:');
                  if (!password) return;
                  
                  const phone = prompt('Enter chef phone number (optional):') || '';
                  
                  const code = prompt('Enter master admin code (3357):');
                  if (!code) return;
                  
                  try {
                    const response = await axios.post('/admin/create-chef', {
                      name,
                      email,
                      password,
                      phone,
                      master_code: code
                    });
                    alert(`✅ Chef Created!\n\nName: ${name}\nEmail: ${email}\nPassword: ${password}\n\nChef can now login and set up their profile.`);
                    fetchData();
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Failed to create chef');
                  }
                }}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
              >
                + Add Chef
              </button>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">👥 User Management</h3>
            <p className="text-sm text-blue-800 mb-2">
              Change user roles to grant different permissions:
            </p>
            <ul className="text-sm text-blue-700 space-y-1 ml-4">
              <li>• <strong>Customer</strong> - Can book chefs and bartenders, manage bookings</li>
              <li>• <strong>Chef</strong> - Can create profile, manage bookings, upload receipts</li>
              <li>• <strong>Bartender</strong> - Can create profile, upload bar photos, manage bookings</li>
              <li>• <strong>Admin</strong> - Full access to manage all users, bookings, and issue cards</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-sm text-blue-800 font-semibold mb-1">🔐 Admin Hierarchy:</p>
              <ul className="text-sm text-blue-700 space-y-1 ml-4">
                <li>• <strong>⭐ Master Admin (Code: 3357)</strong> - Can appoint any admin level</li>
                <li>• <strong>👔 Admin Manager</strong> - Manage users & bookings (cannot promote to admin)</li>
                <li>• <strong>Regular Admin</strong> - Standard admin access</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2 italic">
                💡 Master code required to promote users to admin roles
              </p>
            </div>
            {user.admin_level === 'master_admin' && (
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-sm text-blue-800 font-semibold mb-1">💎 Master Admin: Free Subscriptions</p>
                <ul className="text-sm text-blue-700 space-y-1 ml-4">
                  <li>• <strong>Grant Free Sub</strong> - Chef gets full access without paying $79.99/month</li>
                  <li>• <strong>Revoke Free Sub</strong> - Chef must pay like everyone else</li>
                  <li>• Only you (Master Admin) can grant/revoke free subscriptions</li>
                </ul>
                <p className="text-xs text-blue-600 mt-2 italic">
                  💡 Use this to reward special chefs or for promotional purposes
                </p>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-sm text-blue-800 font-semibold mb-1">🗑️ User Deletion</p>
              <ul className="text-sm text-blue-700 space-y-1 ml-4">
                <li>• Click <strong>Delete User</strong> to permanently remove any user account</li>
                <li>• Deletes all related data: bookings, profiles, receipts</li>
                <li>• Admin accounts require extra confirmation</li>
                <li>• You cannot delete your own account</li>
              </ul>
              <p className="text-xs text-red-600 mt-2 italic font-semibold">
                ⚠️ User deletion is PERMANENT and cannot be undone
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="users-section">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{u.name}</div>
                    {/* SafeServ Status for Chefs */}
                    {u.role === 'chef' && chefProfiles[u.id] && chefProfiles[u.id].safeserv_expiration_date && (
                      <div className="text-xs mt-1">
                        {(() => {
                          const expDate = new Date(chefProfiles[u.id].safeserv_expiration_date);
                          const today = new Date();
                          const daysUntilExpiry = Math.floor((expDate - today) / (1000 * 60 * 60 * 24));
                          
                          if (daysUntilExpiry < 0) {
                            return (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-semibold">
                                🚫 SafeServ EXPIRED
                              </span>
                            );
                          } else if (daysUntilExpiry <= 30) {
                            return (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-semibold">
                                ⚠️ SafeServ Expires in {daysUntilExpiry} days
                              </span>
                            );
                          } else {
                            return (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                                ✅ SafeServ Valid ({expDate.toLocaleDateString()})
                              </span>
                            );
                          }
                        })()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {u.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        u.role === 'chef' ? 'bg-green-100 text-green-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {u.role}
                      </span>
                      {u.admin_level && (
                        <span className="text-xs text-purple-600 font-medium">
                          {u.admin_level === 'master_admin' ? '⭐ Master' : '👔 Manager'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex flex-col gap-1">
                      <div>
                        {u.is_active ? '✅ Active' : '❌ Inactive'}
                        {u.test_account && (
                          <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">TEST</span>
                        )}
                      </div>
                      {u.role === 'chef' && chefProfiles[u.id] && (
                        <div>
                          {chefProfiles[u.id].subscription_id === 'master_admin_free_subscription' ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded font-semibold">
                              💎 FREE SUB
                            </span>
                          ) : chefProfiles[u.id].subscription_active ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              📅 Paid Sub
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                              ⚠️ No Sub
                            </span>
                          )}
                        </div>
                      )}
                      {u.role === 'bartender' && bartenderProfiles[u.id] && (
                        <div>
                          {bartenderProfiles[u.id].subscription_id === 'master_admin_free_subscription' ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded font-semibold">
                              💎 FREE SUB
                            </span>
                          ) : bartenderProfiles[u.id].subscription_active ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              📅 Paid Sub
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                              ⚠️ No Sub
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <RoleChanger userId={u.id} currentRole={u.role} currentAdminLevel={u.admin_level} userName={u.name} onUpdate={fetchData} />
                      
                      {/* Master Admin: Free Subscription Management for Chefs */}
                      {u.role === 'chef' && user.admin_level === 'master_admin' && (
                        <>
                          {chefProfiles[u.id]?.subscription_id === 'master_admin_free_subscription' ? (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Revoke free subscription from ${u.name}?\n\nThey will need to pay $79.99/month like everyone else.`)) return;
                                const code = prompt('Enter master admin code (3357):');
                                if (!code) return;
                                try {
                                  await axios.post(`/admin/revoke-free-subscription/${u.id}?master_code=${code}`);
                                  alert('✅ Free subscription revoked. Chef must now pay.');
                                  fetchData();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Failed to revoke');
                                }
                              }}
                              className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-semibold"
                              data-testid={`revoke-subscription-${u.id}`}
                              title="Revoke free subscription"
                            >
                              🚫 Revoke Free Sub
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Grant FREE subscription to ${u.name}?\n\nThey won't have to pay $79.99/month.`)) return;
                                const code = prompt('Enter master admin code (3357):');
                                if (!code) return;
                                try {
                                  await axios.post(`/admin/grant-free-subscription/${u.id}?master_code=${code}`);
                                  alert('✅ Free subscription granted!');
                                  fetchData();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Failed to grant');
                                }
                              }}
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold"
                              data-testid={`grant-subscription-${u.id}`}
                              title="Grant free subscription"
                            >
                              ✨ Grant Free Sub
                            </button>
                          )}
                        </>
                      )}
                      
                      {/* Master Admin: Free Subscription Management for Bartenders */}
                      {u.role === 'bartender' && user.admin_level === 'master_admin' && (
                        <>
                          {bartenderProfiles[u.id]?.subscription_id === 'master_admin_free_subscription' ? (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Revoke free subscription from bartender ${u.name}?\n\nThey will need to pay $30/month like everyone else.`)) return;
                                const code = prompt('Enter master admin code (3357):');
                                if (!code) return;
                                try {
                                  await axios.post(`/admin/revoke-free-bartender-subscription/${u.id}?master_code=${code}`);
                                  alert('✅ Free subscription revoked. Bartender must now pay.');
                                  fetchData();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Failed to revoke');
                                }
                              }}
                              className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-semibold"
                              data-testid={`revoke-bartender-subscription-${u.id}`}
                              title="Revoke free subscription"
                            >
                              🚫 Revoke Free Sub
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Grant FREE subscription to bartender ${u.name}?\n\nThey won't have to pay $30/month.`)) return;
                                const code = prompt('Enter master admin code (3357):');
                                if (!code) return;
                                try {
                                  await axios.post(`/admin/grant-free-bartender-subscription/${u.id}?master_code=${code}`);
                                  alert('✅ Free subscription granted!');
                                  fetchData();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Failed to grant');
                                }
                              }}
                              className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs font-semibold"
                              data-testid={`grant-bartender-subscription-${u.id}`}
                              title="Grant free subscription"
                            >
                              ✨ Grant Free Sub
                            </button>
                          )}
                        </>
                      )}

                      {/* Discount Buttons - Only for Customers */}
                      {u.role === 'customer' && (
                        <div className="flex gap-1 flex-wrap">
                          {/* Show current discount if exists */}
                          {u.discount_percent && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                              {u.discount_percent}% OFF {u.discount_duration === 'lifetime' ? '(Lifetime)' : '(1 Year)'}
                            </span>
                          )}
                          
                          {/* 10% OFF */}
                          <button
                            onClick={async () => {
                              const duration = window.confirm('Apply 10% discount for LIFETIME?\n\nClick OK for Lifetime\nClick Cancel for 1 Year') ? 'lifetime' : 'year';
                              if (!window.confirm(`Apply 10% discount to ${u.name} for ${duration}?`)) return;
                              try {
                                await axios.post(`/admin/apply-discount/${u.id}?discount_percent=10&duration=${duration}`);
                                alert(`✅ 10% discount applied for ${duration}`);
                                fetchData();
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Failed to apply discount');
                              }
                            }}
                            className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs font-semibold"
                            title="Apply 10% discount"
                          >
                            10% OFF
                          </button>

                          {/* 15% OFF */}
                          <button
                            onClick={async () => {
                              const duration = window.confirm('Apply 15% discount for LIFETIME?\n\nClick OK for Lifetime\nClick Cancel for 1 Year') ? 'lifetime' : 'year';
                              if (!window.confirm(`Apply 15% discount to ${u.name} for ${duration}?`)) return;
                              try {
                                await axios.post(`/admin/apply-discount/${u.id}?discount_percent=15&duration=${duration}`);
                                alert(`✅ 15% discount applied for ${duration}`);
                                fetchData();
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Failed to apply discount');
                              }
                            }}
                            className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-semibold"
                            title="Apply 15% discount"
                          >
                            15% OFF
                          </button>

                          {/* 20% OFF */}
                          <button
                            onClick={async () => {
                              const duration = window.confirm('Apply 20% discount for LIFETIME?\n\nClick OK for Lifetime\nClick Cancel for 1 Year') ? 'lifetime' : 'year';
                              if (!window.confirm(`Apply 20% discount to ${u.name} for ${duration}?`)) return;
                              try {
                                await axios.post(`/admin/apply-discount/${u.id}?discount_percent=20&duration=${duration}`);
                                alert(`✅ 20% discount applied for ${duration}`);
                                fetchData();
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Failed to apply discount');
                              }
                            }}
                            className="px-2 py-1 bg-blue-700 text-white rounded hover:bg-blue-800 text-xs font-semibold"
                            title="Apply 20% discount"
                          >
                            20% OFF
                          </button>

                          {/* 50% OFF - Master Admin Only */}
                          {user.admin_level === 'master_admin' && (
                            <button
                              onClick={async () => {
                                const masterCode = prompt('Enter Master Admin Code to apply 50% discount:');
                                if (!masterCode) return;
                                const duration = window.confirm('Apply 50% discount for LIFETIME?\n\nClick OK for Lifetime\nClick Cancel for 1 Year') ? 'lifetime' : 'year';
                                if (!window.confirm(`Apply 50% discount to ${u.name} for ${duration}?`)) return;
                                try {
                                  await axios.post(`/admin/apply-discount/${u.id}?discount_percent=50&duration=${duration}&master_code=${masterCode}`);
                                  alert(`✅ 50% discount applied for ${duration}`);
                                  fetchData();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Failed to apply discount');
                                }
                              }}
                              className="px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs font-semibold"
                              title="Master Admin: Apply 50% discount"
                            >
                              50% OFF
                            </button>
                          )}

                          {/* Remove Discount */}
                          {u.discount_percent && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Remove ${u.discount_percent}% discount from ${u.name}?`)) return;
                                try {
                                  await axios.delete(`/admin/remove-discount/${u.id}`);
                                  alert(`✅ Discount removed`);
                                  fetchData();
                                } catch (err) {
                                  alert(err.response?.data?.detail || 'Failed to remove discount');
                                }
                              }}
                              className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs font-semibold"
                              title="Remove discount"
                            >
                              Remove Discount
                            </button>
                          )}
                        </div>
                      )}

                      {/* Override Certification Button - Master Admin Only for Chefs */}
                      {u.role === 'chef' && user.admin_level === 'master_admin' && (
                        <button
                          onClick={async () => {
                            const masterCode = prompt('Enter Master Admin Code (3357) to override SafeServ:');
                            if (!masterCode) return;
                            
                            const certNumber = prompt(`Update SafeServ Cert Number for ${u.name}:`, '');
                            if (!certNumber) return;
                            
                            const expirationDate = prompt('Expiration Date (YYYY-MM-DD):', '');
                            if (!expirationDate) return;
                            
                            try {
                              await axios.post(`/admin/update-safeserv/${u.id}`, {
                                safeserv_cert_number: certNumber,
                                safeserv_expiration_date: expirationDate
                              }, {
                                params: { master_code: masterCode }
                              });
                              alert(`✅ SafeServ updated for ${u.name}`);
                              fetchData();
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to update SafeServ');
                            }
                          }}
                          className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold"
                          title="Master Admin: Override SafeServ"
                        >
                          📜 Update SafeServ
                        </button>
                      )}

                      {/* Override License Button - Master Admin Only for Bartenders */}
                      {u.role === 'bartender' && user.admin_level === 'master_admin' && (
                        <button
                          onClick={async () => {
                            const masterCode = prompt('Enter Master Admin Code (3357) to override License/LLC:');
                            if (!masterCode) return;
                            
                            const licenseNumber = prompt(`Update License Number for ${u.name}:`, '');
                            const llcNumber = prompt('Update LLC Number (optional):', '');
                            
                            if (!licenseNumber && !llcNumber) {
                              alert('Please enter at least one value');
                              return;
                            }
                            
                            try {
                              const data = {};
                              if (licenseNumber) data.license_number = licenseNumber;
                              if (llcNumber) data.llc_number = llcNumber;
                              
                              await axios.post(`/admin/update-bartender-license/${u.id}?master_code=${masterCode}`, data);
                              alert(`✅ License/LLC updated for ${u.name}`);
                              fetchData();
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to update license');
                            }
                          }}
                          className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold"
                          title="Master Admin: Override License/LLC"
                        >
                          🪪 Update License
                        </button>
                      )}
                      
                      {/* Reset Password Button */}
                      {u.id !== user.id && (
                        <button
                          onClick={async () => {
                            const newPassword = prompt(`Enter new password for ${u.name}:`);
                            if (!newPassword) return;
                            if (newPassword.length < 6) {
                              alert('Password must be at least 6 characters');
                              return;
                            }
                            if (!window.confirm(`Reset password for ${u.name} to:\n${newPassword}`)) return;
                            try {
                              const response = await axios.post(`/admin/reset-password/${u.id}?new_password=${encodeURIComponent(newPassword)}`);
                              alert(`✅ Password reset!\n\nEmail: ${u.email}\nNew Password: ${newPassword}\n\nUser can now login with this password.`);
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to reset password');
                            }
                          }}
                          className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 text-xs font-semibold"
                          title="Reset user password"
                        >
                          🔑 Reset Password
                        </button>
                      )}
                      
                      {/* Delete User Button - Available for all users except yourself */}
                      {u.id !== user.id && (
                        <button
                          onClick={async () => {
                            // Extra confirmation for admin deletion
                            const isAdmin = u.role === 'admin';
                            const confirmMsg = isAdmin 
                              ? `⚠️ WARNING: Delete ADMIN account ${u.name} (${u.email})?\n\nThis will permanently delete:\n- Their admin account\n- All related data\n\nThis action CANNOT be undone!`
                              : `Delete ${u.role} account: ${u.name} (${u.email})?\n\nThis will permanently delete:\n- Their account\n- All bookings${u.role === 'chef' ? '\n- Chef profile' : ''}\n- All related data\n\nThis action CANNOT be undone!`;
                            
                            if (!window.confirm(confirmMsg)) return;
                            
                            // Second confirmation for admin deletion
                            if (isAdmin) {
                              if (!window.confirm('Are you ABSOLUTELY SURE you want to delete this admin account?')) return;
                            }
                            
                            try {
                              await axios.delete(`/admin/user/delete/${u.id}`);
                              alert(`✅ User ${u.name} deleted successfully`);
                              fetchData();
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to delete user');
                            }
                          }}
                          className={`px-3 py-1 ${u.role === 'admin' ? 'bg-red-700' : 'bg-red-600'} text-white rounded hover:bg-red-800 text-xs font-semibold`}
                          data-testid={`delete-user-${u.id}`}
                          title={`Delete ${u.name}'s account`}
                        >
                          🗑️ Delete {u.role === 'admin' ? 'Admin' : 'User'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Catering Tab */}
      {activeTab === 'catering' && (
        <AdminCateringPanel />
      )}

      {/* Chef Gallery Review Tab - Master Admin Only */}
      {activeTab === 'chef_gallery' && user.admin_level === 'master_admin' && (
        <MasterChefGalleryReview />
      )}

      {/* Banking Settings Tab - Master Admin Only */}
      {activeTab === 'banking' && user.admin_level === 'master_admin' && (
        <MasterBankingSettings />
      )}
    </div>
  );
};

// Master Admin Banking Settings Component
const MasterBankingSettings = () => {
  const [bankingInfo, setBankingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    routing_number: '',
    account_number: '',
    bank_name: '',
    account_type: 'checking',
    ssn_last4: ''
  });

  useEffect(() => {
    fetchBankingInfo();
  }, []);

  const fetchBankingInfo = async () => {
    const masterCode = prompt('Enter Master Admin Code to view banking settings:');
    if (!masterCode) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`/admin/master-banking-info?master_code=${masterCode}`);
      if (response.data.has_banking_info) {
        setBankingInfo(response.data);
      }
      setLoading(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load banking info');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const masterCode = prompt('Enter Master Admin Code to save banking information:');
    if (!masterCode) return;

    // Validate fields
    if (!formData.account_holder_name || !formData.routing_number || !formData.account_number) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.routing_number.length !== 9) {
      alert('Routing number must be 9 digits');
      return;
    }

    if (!window.confirm('Save banking information? This is where platform fees ($79.99 chef signup, $30 bartender signup, $26 booking fees) will be deposited.')) {
      return;
    }

    setSaving(true);
    try {
      await axios.post(`/admin/master-banking-info?master_code=${masterCode}`, formData);
      alert('✅ Banking information saved successfully!');
      fetchBankingInfo();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save banking info');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading banking settings...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">💰 Master Admin Banking Settings</h2>
        <p className="text-gray-600 mt-2">
          All platform fees will be deposited to this account:
        </p>
        <ul className="text-sm text-gray-600 mt-2 list-disc list-inside">
          <li>Chef signup: $79.99/month</li>
          <li>Bartender signup: $30/month</li>
          <li>Booking fee: $26 per booking</li>
        </ul>
      </div>

      {/* Current Banking Info */}
      {bankingInfo && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-green-900 mb-2">✅ Banking Information on File</h3>
          <div className="text-sm text-green-800">
            <p><strong>Account Holder:</strong> {bankingInfo.account_holder_name}</p>
            <p><strong>Bank:</strong> {bankingInfo.bank_name || 'Not specified'}</p>
            <p><strong>Account Type:</strong> {bankingInfo.account_type}</p>
            <p><strong>Account Number:</strong> ****{bankingInfo.account_number_last4}</p>
          </div>
          <p className="text-xs text-green-600 mt-2">Update the form below to change banking information</p>
        </div>
      )}

      {/* Banking Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Holder Name *
          </label>
          <input
            type="text"
            value={formData.account_holder_name}
            onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="Full name on account"
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Routing Number * (9 digits)
            </label>
            <input
              type="text"
              value={formData.routing_number}
              onChange={(e) => setFormData({...formData, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9)})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="123456789"
              maxLength="9"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number *
            </label>
            <input
              type="text"
              value={formData.account_number}
              onChange={(e) => setFormData({...formData, account_number: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Account number"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name (optional)
            </label>
            <input
              type="text"
              value={formData.bank_name}
              onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="e.g., Chase, Bank of America"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Type *
            </label>
            <select
              value={formData.account_type}
              onChange={(e) => setFormData({...formData, account_type: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SSN Last 4 Digits (optional - for verification)
          </label>
          <input
            type="text"
            value={formData.ssn_last4}
            onChange={(e) => setFormData({...formData, ssn_last4: e.target.value.replace(/\D/g, '').slice(0, 4)})}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            placeholder="1234"
            maxLength="4"
          />
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>⚠️ Security Note:</strong> Banking information is stored securely. In production, this integrates with Stripe Connect for actual fund transfers.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg disabled:opacity-50"
        >
          {saving ? 'Saving...' : '💰 Save Banking Information'}
        </button>
      </form>
    </div>
  );
};

export default AdminDashboard;
